import { useEffect, useMemo, useRef, useState } from 'react'
import type { DragEvent } from 'react'
import {
  ApiError,
  UnauthorizedError,
  documentsApi,
  type CatalogDocumentDTO,
} from '@/shared/api'
import { Icon } from '@/shared/ui/Icon'
import { BetaUploadNotice } from '@/shared/ui/BetaUploadNotice'
import { DocumentMetadataForm } from './DocumentMetadataForm'
import {
  ACCEPTED_FILE_EXTENSIONS,
  ACCEPTED_FILE_LABEL,
  DEFAULT_UPLOAD_METADATA,
  deriveDocumentIdFromFilename,
  documentTitle,
  findDuplicateDocument,
  formatBytes,
} from './documentUtils'
import type {
  UploadFormErrors,
  UploadFormValues,
  UploadSuccessSummary,
} from './types'

interface Props {
  tenantId: string
  organizationLabel: string
  /** Existing catalog so we can warn about duplicate document_ids up-front. */
  existingDocuments: CatalogDocumentDTO[]
  /** Fired after a successful upload so the parent can refresh the list. */
  onUploaded: (summary: UploadSuccessSummary) => void
}

const INITIAL_VALUES: UploadFormValues = {
  file: null,
  entityType: DEFAULT_UPLOAD_METADATA.entityType,
  entityId: DEFAULT_UPLOAD_METADATA.entityId,
  groupType: DEFAULT_UPLOAD_METADATA.groupType,
  groupId: DEFAULT_UPLOAD_METADATA.groupId,
  documentId: '',
  entityDisplayName: '',
  groupDisplayName: '',
  title: '',
}

function validate(values: UploadFormValues): UploadFormErrors {
  const errors: UploadFormErrors = {}
  if (!values.file) errors.file = 'Selecione um arquivo.'
  if (!values.entityType.trim()) errors.entityType = 'Obrigatório.'
  if (!values.entityId.trim()) errors.entityId = 'Obrigatório.'

  const groupTypeFilled = values.groupType.trim().length > 0
  const groupIdFilled = values.groupId.trim().length > 0
  if (groupTypeFilled !== groupIdFilled) {
    const message = 'Preencha tipo e ID do grupo juntos.'
    errors.groupType = message
    errors.groupId = message
  }

  return errors
}

function errorDetail(err: unknown, fallback: string): string {
  if (err instanceof ApiError) return err.detail || fallback
  if (err instanceof Error) return err.message
  return fallback
}

/**
 * Upload card. Wraps the legacy admin flow (RAG/frontend UploadForm) but:
 *   - tenant_id is derived from auth (never user-editable),
 *   - cookie-only API client (no apiKey props, no header juggling),
 *   - errors mapped to inline field/form messages,
 *   - duplicate document_id is detected client-side from the catalog snapshot
 *     and requires explicit confirmation before submit (backend
 *     ``create_or_reset_document`` silently overwrites, so the UI must make
 *     that consequence visible — STEP 7.9B / section C),
 *   - success state offers a clear "ver na lista" affordance.
 */
export function DocumentUploadPanel({
  tenantId,
  organizationLabel,
  existingDocuments,
  onUploaded,
}: Props) {
  const [values, setValues] = useState<UploadFormValues>(INITIAL_VALUES)
  const [errors, setErrors] = useState<UploadFormErrors>({})
  const [isDragging, setIsDragging] = useState(false)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [success, setSuccess] = useState<UploadSuccessSummary | null>(null)
  const [confirmReplace, setConfirmReplace] = useState(false)
  /**
   * True once the user has manually edited the document_id field. While
   * false, the field auto-fills from the chosen filename; once true, swapping
   * the file no longer overwrites the user's edit.
   */
  const [documentIdTouched, setDocumentIdTouched] = useState(false)
  const fileInputRef = useRef<HTMLInputElement>(null)

  function updateField(field: keyof UploadFormValues, value: string) {
    setValues((prev) => ({ ...prev, [field]: value }))
    setErrors((prev) => ({ ...prev, [field]: undefined, form: undefined }))
    setSuccess(null)
    if (field === 'documentId') {
      setDocumentIdTouched(value.trim().length > 0)
      setConfirmReplace(false)
    }
    if (
      field === 'entityType' ||
      field === 'entityId' ||
      field === 'groupType' ||
      field === 'groupId'
    ) {
      // Scope changed → previous "replace" confirmation no longer applies.
      setConfirmReplace(false)
    }
  }

  function handleFile(file: File | null | undefined) {
    if (!file) return
    setValues((prev) => {
      const next: UploadFormValues = { ...prev, file }
      if (!documentIdTouched) {
        next.documentId = deriveDocumentIdFromFilename(file.name)
      }
      return next
    })
    setErrors((prev) => ({ ...prev, file: undefined, form: undefined }))
    setSuccess(null)
    setConfirmReplace(false)
  }

  function handleDrop(event: DragEvent<HTMLDivElement>) {
    event.preventDefault()
    setIsDragging(false)
    const file = event.dataTransfer.files?.[0]
    if (file) handleFile(file)
  }

  function resetForm() {
    setValues(INITIAL_VALUES)
    setErrors({})
    setDocumentIdTouched(false)
    setConfirmReplace(false)
    if (fileInputRef.current) fileInputRef.current.value = ''
  }

  // The effective document_id we'd send if the user submitted right now —
  // either their typed value or the slug we derived from the filename.
  const effectiveDocumentId = useMemo(() => {
    const typed = values.documentId.trim()
    if (typed) return typed
    return values.file ? deriveDocumentIdFromFilename(values.file.name) : ''
  }, [values.documentId, values.file])

  // Step 13.7-FIX — duplicate is keyed on tenant + document_id (the key the
  // backend uses for selection/delete/retrieval), not the full entity/group
  // tuple. Returns the matched row so the warning can name it.
  const duplicateDocument = useMemo(
    () => findDuplicateDocument(existingDocuments, tenantId, effectiveDocumentId),
    [existingDocuments, tenantId, effectiveDocumentId],
  )
  const isDuplicate = duplicateDocument !== null

  // If the duplicate goes away (user changed id/scope), drop the confirmation.
  useEffect(() => {
    if (!isDuplicate && confirmReplace) setConfirmReplace(false)
  }, [isDuplicate, confirmReplace])

  async function handleSubmit(event: React.FormEvent) {
    event.preventDefault()
    if (isSubmitting) return
    setSuccess(null)

    const validation = validate(values)
    if (Object.keys(validation).length > 0) {
      setErrors(validation)
      return
    }

    if (isDuplicate && !confirmReplace) {
      setErrors({
        form:
          'Já existe um documento com esse ID neste escopo. ' +
          'Marque "Substituir documento existente" para reindexar, ' +
          'ou edite o ID do documento.',
      })
      return
    }

    const file = values.file!
    const documentId =
      values.documentId.trim() || deriveDocumentIdFromFilename(file.name)

    setIsSubmitting(true)
    setErrors({})

    try {
      const response = await documentsApi.uploadDocument({
        file,
        tenantId,
        entityType: values.entityType.trim(),
        entityId: values.entityId.trim(),
        groupType: values.groupType.trim() || null,
        groupId: values.groupId.trim() || null,
        documentId,
        entityDisplayName: values.entityDisplayName.trim() || null,
        groupDisplayName: values.groupDisplayName.trim() || null,
        title: values.title.trim() || null,
      })
      const summary: UploadSuccessSummary = {
        document_id: response.document_id,
        filename: response.filename,
        chunks_indexed: response.chunks_indexed,
        replaced: isDuplicate,
        status: response.status === 'queued' ? 'queued' : 'indexed',
        job_id: response.job_id ?? null,
      }
      setSuccess(summary)
      onUploaded(summary)
      resetForm()
    } catch (err) {
      if (err instanceof UnauthorizedError) {
        setErrors({ form: 'Sessão expirada. Faça login novamente.' })
      } else {
        setErrors({
          form: errorDetail(err, 'Falha ao enviar documento.'),
        })
      }
    } finally {
      setIsSubmitting(false)
    }
  }

  const file = values.file
  const submitDisabled =
    isSubmitting || !file || (isDuplicate && !confirmReplace)
  const submitLabel = isSubmitting
    ? 'Enviando…'
    : isDuplicate
      ? 'Substituir e reindexar'
      : 'Indexar documento'

  return (
    <section className="doc-upload-panel" aria-labelledby="doc-upload-title">
      <header className="doc-upload-head">
        <h2 id="doc-upload-title">Enviar novo documento</h2>
        <p className="doc-upload-sub">
          Workspace: <strong>{organizationLabel}</strong>
          <span className="doc-upload-tenant"> · tenant <code>{tenantId}</code></span>
        </p>
      </header>

      <BetaUploadNotice />

      <form
        className="doc-upload-form"
        onSubmit={(e) => void handleSubmit(e)}
        onKeyDown={(e) => {
          // Block accidental upload when the user presses Enter inside a text
          // metadata field (entity_type, entity_id, group_*, document_id). The
          // submit button itself, the dropzone (role="button"), and the
          // duplicate-replace checkbox still react to keyboard activation, so
          // the upload always requires an explicit affirmation.
          if (e.key !== 'Enter') return
          const target = e.target as HTMLElement
          if (
            target instanceof HTMLInputElement &&
            target.type !== 'submit' &&
            target.type !== 'checkbox' &&
            target.type !== 'file'
          ) {
            e.preventDefault()
          }
        }}
        noValidate
      >
        <div
          className={`doc-dropzone${isDragging ? ' is-dragging' : ''}${
            errors.file ? ' has-error' : ''
          }${isSubmitting ? ' is-uploading' : ''}`}
          onClick={() => !isSubmitting && fileInputRef.current?.click()}
          onDragOver={(e) => {
            if (isSubmitting) return
            e.preventDefault()
            setIsDragging(true)
          }}
          onDragLeave={() => setIsDragging(false)}
          onDrop={(e) => !isSubmitting && handleDrop(e)}
          role="button"
          tabIndex={isSubmitting ? -1 : 0}
          aria-disabled={isSubmitting}
          onKeyDown={(e) => {
            if (isSubmitting) return
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault()
              fileInputRef.current?.click()
            }
          }}
        >
          <div className="doc-dropzone-icon" aria-hidden="true">
            <Icon name="paperclip" />
          </div>
          {file ? (
            <div className="doc-dropzone-file">
              <div className="doc-dropzone-filename">{file.name}</div>
              <div className="doc-dropzone-meta">
                {formatBytes(file.size)} ·{' '}
                {isSubmitting ? 'enviando…' : 'clique para trocar'}
              </div>
            </div>
          ) : (
            <div className="doc-dropzone-prompt">
              <strong>Arraste e solte o arquivo aqui</strong>
              <span>ou clique para selecionar — {ACCEPTED_FILE_LABEL}</span>
            </div>
          )}
          <input
            ref={fileInputRef}
            type="file"
            data-testid="case-upload-file-input"
            accept={ACCEPTED_FILE_EXTENSIONS}
            className="doc-dropzone-input"
            onChange={(e) => handleFile(e.target.files?.[0])}
            disabled={isSubmitting}
          />
          {isSubmitting ? (
            <div className="doc-dropzone-progress" aria-hidden="true">
              <div className="doc-dropzone-progress-bar" />
            </div>
          ) : null}
        </div>
        {errors.file ? (
          <p className="doc-field-error" role="alert">{errors.file}</p>
        ) : null}

        <DocumentMetadataForm
          values={values}
          errors={errors}
          disabled={isSubmitting}
          onChange={updateField}
        />

        {duplicateDocument ? (
          <div className="doc-upload-alert is-warning" role="alert">
            <Icon name="sparkles" />
            <div>
              <strong>Este document_id já existe neste workspace.</strong>
              <p>
                O ID <code>{effectiveDocumentId}</code> corresponde a{' '}
                <strong>{documentTitle(duplicateDocument)}</strong>
                {duplicateDocument.filename &&
                documentTitle(duplicateDocument) !== duplicateDocument.filename
                  ? ` (${duplicateDocument.filename})`
                  : ''}
                . Se continuar com substituição ativada, o documento existente
                será <strong>reindexado</strong>. Altere o document_id para criar
                um novo documento.
              </p>
              <label className="doc-upload-confirm">
                <input
                  type="checkbox"
                  checked={confirmReplace}
                  onChange={(e) => setConfirmReplace(e.target.checked)}
                  disabled={isSubmitting}
                />
                <span>Substituir documento existente</span>
              </label>
            </div>
          </div>
        ) : null}

        {errors.form ? (
          <div className="doc-upload-alert is-error" role="alert">
            <Icon name="close" /> {errors.form}
          </div>
        ) : null}

        {success ? (
          <div className="doc-upload-alert is-success" role="status" data-testid="case-upload-success">
            <Icon name="sparkles" />
            {success.status === 'queued' ? (
              <span>
                <strong>{success.filename}</strong> enviado · na fila para
                processamento · id <code>{success.document_id}</code>. Acompanhe
                em “Ingestões recentes”.
              </span>
            ) : (
              <span>
                <strong>{success.filename}</strong>{' '}
                {success.replaced ? 'reindexado' : 'indexado'} · {success.chunks_indexed}{' '}
                chunks · id <code>{success.document_id}</code>
              </span>
            )}
          </div>
        ) : null}

        <div className="doc-upload-actions">
          <button
            type="submit"
            className={`doc-upload-submit${isDuplicate ? ' is-replace' : ''}`}
            data-testid="case-upload-submit"
            disabled={submitDisabled}
          >
            {submitLabel}
          </button>
        </div>
      </form>
    </section>
  )
}
